import HealthMonitor from '@omni/kit/HealthMonitor';
import {
  KitButton,
  KitIcon,
  KitInput,
  KitListItem,
  KitLoader,
  KitSelect,
  KitSnack,
  KitText,
  KitTouchable,
} from '@omni/kit/components';
import KitMapView from '@omni/kit/components/KitMapView';
import { KitSnackDuration } from '@omni/kit/components/KitSnack.types';
import PlacesAutocomplete from '@omni/kit/components/PlacesAutocomplete';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import {
  WindowSizeMin,
  useSizeClass,
} from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import GroupsService from '@omni/kit/services/GroupsService';
import {
  GroupRoles,
  IGroupAddress,
  IGroupLocation,
} from '@omni/kit/services/GroupsService/Types';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { excludedStatusCodes } from '@omni/kit/utilities/excludedStatusCodes';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { useSelector } from 'react-redux';

import { extractAddressComponents } from '../../../utilities/extractAddressComponents';
import { accessTokenSelector } from '../../redux/selectors';

interface SelectedLocation {
  latitude: number;
  longitude: number;
  address: string;
  streetNumber: string | undefined;
  restOfAddress: string | undefined;
  city: string;
  country: string;
  country_obj: {
    code: string;
    name: string;
  };
  postal_code: string;
  state: string;
  street: string;
}
interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}
interface GooglePlaceDetails {
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  address_components: AddressComponent[];
  formatted_address: string;
}

interface Props {
  navigation: any;
  route: any;
  onCancel?: () => void;
  setEventGroupLocation?: (
    groupEventLocation: IGroupLocation | undefined
  ) => void;
  groupEventLocation?: IGroupLocation | undefined;
}
export default function GroupEventLocation({
  route,
  navigation,
  onCancel,
  setEventGroupLocation,
  groupEventLocation,
}: Props): ReactElement {
  const { t } = useTranslation();
  const { group, handleSetIsLocationEdited, onSetEventGroupLocation } =
    route.params;
  const accessToken = useSelector(accessTokenSelector);
  const isWeb = Platform.OS === 'web';
  const isIOS = Platform.OS === 'ios';
  const isAndroid = Platform.OS === 'android';
  const { smallestWindowWidth, windowWidth, windowHeight } = useSizeClass();
  const isLandscape = windowHeight < windowWidth;
  const containerPadding = {
    borderRadius: 16,
    padding: isWeb || isLandscape ? Spacing.xl : 0,
    paddingHorizontal:
      (isIOS && !isLandscape) || isAndroid ? Spacing.l : Spacing.xl,
    paddingTop: (isIOS && !isLandscape) || isAndroid ? Spacing.m : Spacing.xl,
  };
  const [selectedLocation, setSelectedLocation] =
    useState<SelectedLocation | null>();
  const { tokens, app, user } = useShellContext();

  const isSmall =
    Platform.OS === 'web'
      ? windowWidth < WindowSizeMin.L
      : smallestWindowWidth < WindowSizeMin.L;

  const [eventLocation, setEventLocation] = useState<
    IGroupLocation | undefined
  >();
  const groupEventLocationData: IGroupLocation | undefined = eventLocation
    ? eventLocation
    : isWeb
    ? groupEventLocation
    : route.params.groupEventLocation;

  const [locationNameValue, setLocationNameValue] = useState<
    string | undefined
  >(undefined);
  const streetNumber =
    groupEventLocationData?._embedded?.address?.street &&
    groupEventLocationData?._embedded?.address?.street !== 'undefined'
      ? groupEventLocationData?._embedded?.address?.street?.split(' ')[0]
      : '';

  const isEdited = useMemo(() => {
    const titleName = locationNameValue || undefined;

    if (selectedLocation !== undefined) {
      handleSetIsLocationEdited(true);

      return true;
    }

    if (
      selectedLocation === undefined &&
      groupEventLocationData !== undefined &&
      groupEventLocationData?.title !== titleName
    ) {
      handleSetIsLocationEdited(true);

      return true;
    }

    return false;
  }, [
    selectedLocation,
    locationNameValue,
    groupEventLocationData,
    handleSetIsLocationEdited,
  ]);

  const [loading, setLoading] = useState<boolean>(false);

  const endUserId = user?.id;
  const members = isWeb
    ? group?._embedded?.['group-members']
    : route.params?.group?._embedded?.['group-members'] || [];

  // Compute managers directly
  const managers = Array.isArray(members)
    ? members.filter(
        (member: { role: string }) => member.role === GroupRoles.Manager
      )
    : [];

  // Compute myselfAsMember directly
  const myselfAsMember = Array.isArray(managers)
    ? managers.find(
        (manager: { _embedded: { [x: string]: { id: string | undefined } } }) =>
          manager._embedded?.['end-user']?.id === endUserId
      )
    : undefined;

  const handleSave = useCallback(async () => {
    const updateGroupLocation = async (locationId: string) => {
      const response = await GroupsService.UpdateGroupLocation({
        body: {
          title: locationNameValue,
          _embedded: groupEventLocationData?._embedded,
        },
        id: locationId,
        token: accessToken,
      });

      if (response.status === 200 && response.body) {
        if (Platform.OS !== 'web') {
          navigation.navigate('create_event', {
            groupEventLocation: response.body,
          });
        }

        setEventGroupLocation?.(response.body);
        onCancel?.();
        KitSnack.show(
          t('messaging:successUpdateGroupEventLocationMessage'),
          KitSnackDuration.SHORT
        );
      } else if (
        response.status &&
        !excludedStatusCodes.includes(response.status)
      ) {
        HealthMonitor.logError(response.message);
        KitSnack.show(t('messaging:errorMessage'), KitSnackDuration.SHORT);
      }
    };

    if (isEdited && groupEventLocationData) {
      updateGroupLocation(groupEventLocationData.id);
    }

    try {
      if (accessToken && myselfAsMember.id && selectedLocation) {
        const addressData: Partial<IGroupAddress> = {
          app_key: app.appKey,
          city: selectedLocation.city,
          country: selectedLocation.country,
          country_obj: selectedLocation?.country_obj,
          latitude: selectedLocation?.latitude,
          longitude: selectedLocation?.longitude,
          postal_code: selectedLocation?.postal_code,
          state: selectedLocation?.state,
          street: selectedLocation?.street,
          _embedded: {
            manager: {
              id: myselfAsMember.id,
            },
          },
        };

        const addressResponse = await GroupsService.CreateGroupAddress({
          body: addressData,
          token: accessToken,
        });

        if (addressResponse.body) {
          const locationData: Partial<any> = {
            app_key: app.appKey,
            title: locationNameValue,
            subtitle: selectedLocation.address,
            type: ['group'],
            _embedded: {
              address: {
                id: addressResponse.body.id,
              },
            },
          };

          const locationResponse = await GroupsService.CreateGroupLocation({
            body: locationData,
            token: accessToken,
          });

          if (locationResponse.status === 201 && locationResponse.body) {
            const locationDataToPass: Partial<any> = {
              id: locationResponse.body.id,
              app_key: app.appKey,
              title: locationNameValue,
              subtitle: selectedLocation.address,
              type: ['group'],
              _embedded: {
                address: addressResponse.body,
              },
            };
            onSetEventGroupLocation(false);
            setEventGroupLocation?.(locationDataToPass as IGroupLocation);
            setEventLocation(locationDataToPass as IGroupLocation);
            if (Platform.OS !== 'web') {
              navigation.navigate('create_event', {
                groupEventLocation: locationDataToPass as IGroupLocation,
              });
            }

            onCancel?.();
            KitSnack.show(
              t('messaging:successCreateGroupEventLocationMessage'),
              KitSnackDuration.SHORT
            );
          } else if (
            locationResponse.status &&
            !excludedStatusCodes.includes(locationResponse.status)
          ) {
            HealthMonitor.logError(locationResponse.message);

            KitSnack.show(t('messaging:errorMessage'), KitSnackDuration.SHORT);
          }
        } else if (
          addressResponse.status &&
          !excludedStatusCodes.includes(addressResponse.status)
        ) {
          HealthMonitor.logError(addressResponse.message);

          KitSnack.show(t('messaging:errorMessage'), KitSnackDuration.SHORT);
        }
      }
    } catch (error) {
      HealthMonitor.logError(error);
      KitSnack.show(t('messaging:errorMessage'), KitSnackDuration.SHORT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEdited,
    accessToken,
    route.params.group,
    selectedLocation,
    locationNameValue,
    myselfAsMember,
    navigation,
  ]);
  useEffect(() => {
    if (groupEventLocationData?.title) {
      // Only update the value if it's not already set
      setLocationNameValue(groupEventLocationData.title);
    }
  }, [groupEventLocationData?.title]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      navigation.setOptions({
        headerRight: () => (
          <KitTouchable
            onPress={handleSave}
            disabled={!isEdited}
            style={{
              paddingLeft: 12,
              paddingRight: Platform.OS === 'ios' ? 12 : 18,
              paddingVertical: 6,
              marginRight: -6,
              borderRadius: 40,
            }}
          >
            <KitText
              color={isEdited ? Colors.brand : Colors.N400}
              fontSize={16}
            >
              {t('common:buttonTitleSave')}
            </KitText>
          </KitTouchable>
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdited, handleSave, navigation]);

  const extractStreetName = (address: any) => {
    if (typeof address !== 'string' || address.trim() === '') {
      return undefined;
    }

    // Use a regular expression to remove the leading street number
    return address.replace(/^\d+\s*,?\s*/, '').trim();
  };

  const processAddressComponents = (
    addressComponents: any[],
    latitude: number,
    longitude: number,
    description: string
  ) => {
    const locationData = extractAddressComponents(
      addressComponents,
      latitude,
      longitude,
      description
    ) as SelectedLocation;
    setSelectedLocation(locationData);
  };

  const handleSelectLocation = (
    data: any,
    details: GooglePlaceDetails | null
  ) => {
    if (Platform.OS !== 'web' && details) {
      processAddressComponents(
        details.address_components,
        details.geometry.location.lat,
        details.geometry.location.lng,
        details.formatted_address
      );
    }

    if (Platform.OS === 'web' && details) {
      const latitude =
        typeof details.geometry.location.lat === 'function'
          ? // @ts-ignore
            details.geometry.location.lat()
          : details.geometry.location.lat;
      const longitude =
        typeof details?.geometry.location.lat === 'function'
          ? // @ts-ignore
            details.geometry.location.lng()
          : details.geometry.location.lng;
      processAddressComponents(
        details.address_components,
        latitude,
        longitude,
        details.formatted_address
      );
    }
  };
  const handleRemoveLocation = useCallback(async () => {
    try {
      if (!groupEventLocationData && selectedLocation) {
        if (Platform.OS !== 'web') {
          navigation.navigate('create_event', {
            groupEventLocation: undefined,
          });
        }

        onCancel?.();
      }

      if (groupEventLocationData !== undefined) {
        const response = await GroupsService.DeleteGroupLocation({
          id: groupEventLocationData.id,
          token: accessToken,
        });

        if (response.status === 204) {
          if (Platform.OS !== 'web') {
            navigation.navigate('create_event', {
              groupEventLocation: undefined,
            });
            handleSetIsLocationEdited(true);
            onSetEventGroupLocation(true);
          }

          handleSetIsLocationEdited(true);
          onSetEventGroupLocation(true);
          setEventLocation(undefined);
          setEventGroupLocation?.(undefined);
          onCancel?.();
        } else {
          if (
            response.status &&
            !excludedStatusCodes.includes(response.status)
          ) {
            HealthMonitor.logError(response.message);
          }

          setSelectedLocation(undefined);
        }
      }
    } catch (error) {
      HealthMonitor.logError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accessToken,
    selectedLocation,
    groupEventLocationData,
    onCancel,
    setEventGroupLocation,
  ]);

  return (
    <>
      <View style={[styles.container, containerPadding]}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <KitText color={Colors.N900} style={styles.title}>
            {t('messaging:groupEventLocationTitle')}
          </KitText>
        </View>
        {loading ? (
          <View
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: Colors.N0,
            }}
          >
            <KitLoader />
          </View>
        ) : (selectedLocation || groupEventLocationData?._embedded) &&
          !loading ? (
          <>
            <KeyboardAvoidingView
              behavior={Platform.OS === 'ios' ? 'position' : 'height'}
              keyboardVerticalOffset={isLandscape ? 40 : 80}
            >
              <ScrollView>
                <View
                  style={{ overflow: 'hidden', borderRadius: 8 }}
                  testID='groupEventLocationMap'
                >
                  <KitMapView
                    latitude={
                      selectedLocation?.latitude ||
                      groupEventLocationData?._embedded.address?.latitude
                    }
                    longitude={
                      selectedLocation?.longitude ||
                      groupEventLocationData?._embedded.address?.longitude
                    }
                    marker={{}}
                    webStyle={{ height: 176, borderRadius: 8 }}
                    nativeStyle={{ height: 176, borderRadius: 8 }}
                  />
                  <KitListItem
                    title={selectedLocation?.streetNumber || streetNumber}
                    subtitle={extractStreetName(
                      selectedLocation?.restOfAddress ||
                        groupEventLocationData?.subtitle
                    )}
                    topBorder={false}
                    style={{ marginTop: Spacing.m }}
                    ImageComponent={
                      <KitIcon
                        name='not-filled-location'
                        size={20}
                        color={Colors.N600}
                        style={{ marginLeft: Spacing.s }}
                      />
                    }
                    testID='groupEventLocationAddress'
                  />
                  <KitInput
                    label={t('messaging:labelLocationNameOptional')}
                    onChangeText={(e) => setLocationNameValue(e)}
                    style={{
                      marginTop: Spacing.l,
                      marginBottom: isWeb ? 0 : Spacing.xl,
                    }}
                    testID='locationName'
                    value={locationNameValue}
                  />
                  {Platform.OS !== 'web' && !isLandscape ? (
                    <KitButton
                      secondary
                      icon='minus-circle'
                      iconSize={14}
                      title={t('messaging:removeLocationButtonLabel')}
                      onPress={handleRemoveLocation}
                    />
                  ) : null}
                  {isWeb || isLandscape ? (
                    <View
                      style={[
                        styles.buttonContainer,
                        isSmall &&
                          !isLandscape && {
                            justifyContent: 'center',
                            alignItems: 'center',
                            paddingHorizontal: 0,
                          },
                        isLandscape && {
                          paddingHorizontal: 0,
                        },
                      ]}
                    >
                      <KitButton
                        secondary
                        icon='minus-circle'
                        iconSize={14}
                        title={t('messaging:removeLocationButtonLabel')}
                        onPress={handleRemoveLocation}
                        style={{
                          marginRight: isSmall && !isLandscape ? 0 : Spacing.s,
                          justifyContent:
                            isSmall && !isLandscape ? 'center' : 'flex-end',
                          width:
                            isSmall && !isLandscape
                              ? '100%'
                              : isLandscape
                              ? '50%'
                              : 'auto',
                        }}
                        testID='removeLocation'
                      />

                      {!isSmall || isLandscape ? (
                        <View style={styles.rightButtonsContainer}>
                          <KitButton
                            secondary
                            title={t('messaging:buttonTitleCancel')}
                            onPress={
                              Platform.OS === 'web'
                                ? onCancel
                                : navigation.goBack
                            }
                            style={styles.rightButton}
                          />
                          <KitButton
                            title={t('common:buttonTitleSave')}
                            color={Colors.N900}
                            onPress={handleSave}
                            style={styles.rightButton}
                            disabled={!isEdited}
                          />
                        </View>
                      ) : null}
                    </View>
                  ) : null}
                </View>
              </ScrollView>
            </KeyboardAvoidingView>
          </>
        ) : (
          <View style={{ height: Platform.OS === 'web' ? 500 : 'auto' }}>
            <PlacesAutocomplete
              onPlaceSelected={handleSelectLocation}
              placeholder={t('messaging:placeholderAddress')}
              testID='locationInput'
            />
          </View>
        )}
      </View>
    </>
  );
}
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.N0,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: Platform.OS === 'web' ? 24 : 18,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    paddingTop: Spacing.xl,
    paddingHorizontal: Platform.OS === 'web' ? 14 : Spacing.xl,
  },
  rightButton: {
    marginLeft: Spacing.s,
    width: 100,
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
});
